import { differenceInCalendarDays, format } from 'date-fns'
import { Mail, Pencil, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { TriggerRow } from '@/components/shared/usage-alert-card/trigger-row'
import { Large, Muted } from '@/components/sv/typographies'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Item, ItemContent, ItemMedia, ItemTitle } from '@/components/ui/item'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'

type Trigger = {
  id: string
  percentage: number
}

type Props = {
  scopeLabel: string
  currentSpendUsd: number
  limitUsd: number
  periodEnd: Date | null
  triggers: Trigger[]
  emails: string[]
  canUpdate: boolean
  onUpdate: () => void
  onDelete: () => void
}

const formatMoney = (amount: number) => `$${amount.toFixed(2)}`

export const UsageAlertConfiguredState = ({
  scopeLabel,
  currentSpendUsd,
  limitUsd,
  periodEnd,
  triggers,
  emails,
  canUpdate,
  onUpdate,
  onDelete,
}: Props) => {
  const sortedTriggers = useMemo(
    () =>
      [...triggers].sort(
        (triggerA, triggerB) => triggerA.percentage - triggerB.percentage
      ),
    [triggers]
  )

  const progress =
    limitUsd > 0 ? Math.min(100, (currentSpendUsd / limitUsd) * 100) : 0

  const daysToReset = periodEnd
    ? Math.max(0, differenceInCalendarDays(periodEnd, new Date()))
    : null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Muted className="text-sm">{scopeLabel} budget</Muted>
          <Large className="text-xl tabular-nums">
            {formatMoney(currentSpendUsd)}
            <span className="text-muted-foreground font-normal">
              {' '}
              / {formatMoney(limitUsd)}
            </span>
          </Large>
        </div>
        {canUpdate && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onUpdate}
              aria-label="Update usage alert"
            >
              <Pencil />
              Update
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              aria-label="Delete usage alert"
            >
              <Trash2 />
              Delete
            </Button>
          </div>
        )}
      </div>

      <Progress
        value={progress}
        className="bg-success/20 [&>[data-slot=progress-indicator]]:bg-success"
      />

      {daysToReset !== null && periodEnd ? (
        <Muted className="text-sm">
          Resets in {daysToReset} {daysToReset === 1 ? 'day' : 'days'} (
          {format(periodEnd, 'MMM d, yyyy')}).
        </Muted>
      ) : null}

      <Separator className="-mx-6 data-[orientation=horizontal]:w-auto" />

      <div className="space-y-2">
        {sortedTriggers.map(({ id, percentage }) => (
          <TriggerRow key={id} percentage={percentage} />
        ))}
      </div>

      <Separator className="-mx-6 data-[orientation=horizontal]:w-auto" />

      <Accordion type="single" collapsible>
        <AccordionItem value="emails" className="border-b-0">
          <AccordionTrigger className="py-2">
            <div className="flex items-center gap-2">
              <span>Email recipients</span>
              <Badge variant="secondary">{emails.length}</Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-0">
            <div className="space-y-2 pt-2">
              {emails.map((email) => (
                <Item key={email} variant="outline" size="sm">
                  <ItemMedia variant="icon">
                    <Mail />
                  </ItemMedia>
                  <ItemContent>
                    <ItemTitle>{email}</ItemTitle>
                  </ItemContent>
                </Item>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
